import {ROLES,NEEDS,newMirrorState,roleFor,roleDraft,chooseRole,chooseNeed,needDraft,needIsValid,permissionFor,permissionIsValid,confirmPermission,resultFor,hasPrivateWriting,privateRecord} from './shadow-mirror-rules.js';
import {encryptedCard} from './shadow-journal-crypto.js';

function downloadHTML(html, win) {
  const url=win.URL.createObjectURL(new win.Blob([html],{type:'text/html;charset=utf-8'}));
  const link=win.document.createElement('a');link.href=url;link.download='shadow-journal-'+new Date().toISOString().slice(0,10)+'.html';
  // Keep the temporary download control inside the replay-excluded tool.
  const parent=win.document.querySelector('[data-shadow-root]') || win.document.body;
  parent.append(link);link.click();link.remove();win.setTimeout(()=>win.URL.revokeObjectURL(url),30000);
}
export function initMirror(root, {win=root?.ownerDocument.defaultView,encrypt=encryptedCard,download=downloadHTML}={}) {
  if (!root || root.dataset.shadowReady) return null;
  const $=s=>root.querySelector(s), all=s=>[...root.querySelectorAll(s)];
  let copy,deck;
  try { copy=JSON.parse($('[data-shadow-copy]').textContent);deck=JSON.parse($('[data-shadow-deck]').textContent); } catch { return null; }
  const controller=new win.AbortController(),on=(node,type,fn)=>node.addEventListener(type,fn,{signal:controller.signal});
  const uid=root.id.replace(/^ShadowMirror-/,'');
  let state=newMirrorState(),busy=false,epoch=0,drag=null,suppressHandleClick=false,savedDisabled=new Map();
  root.dataset.shadowReady='true';
  const status=$('[data-status]'),saveStatus=$('[data-save-status]');
  const say=text=>{ status.textContent=text; };
  const setText=(s,text)=>{ $(s).textContent=text; };
  function error(field,node,text) {
    field.setAttribute('aria-invalid','true');node.textContent=text;node.hidden=false;field.focus({preventScroll:true});
  }
  function clearError(field,node) { field.removeAttribute('aria-invalid');node.textContent='';node.hidden=true; }
  function syncValidity() {
    $('[data-look]').disabled=busy || !state.role;
    $('[data-choose-permission]').disabled=busy || !needIsValid(state);
    $('[data-confirm-permission]').disabled=busy || !permissionIsValid(state);
    $('[data-step="unveil"]').disabled=busy || !state.role;
    $('[data-step="keep"]').disabled=busy || !needIsValid(state);
  }
  function setBusy(value) {
    busy=value;root.setAttribute('aria-busy',String(value));
    if(value) { savedDisabled=new Map(all('button,input,textarea').map(el=>[el,el.disabled]));savedDisabled.forEach((_,el)=>{el.disabled=true;}); }
    else { savedDisabled.forEach((disabled,el)=>{if(el.isConnected)el.disabled=disabled;});savedDisabled.clear();syncValidity(); }
  }
  function closeSave() {
    $('[data-export-panel]').hidden=true;$('[data-open-save]').hidden=false;$('[data-open-save]').setAttribute('aria-expanded','false');
    $('[data-password]').value='';$('[data-password-confirm]').value='';saveStatus.textContent='';
    all('[data-password],[data-password-confirm]').forEach(el=>el.removeAttribute('aria-invalid'));
  }
  function syncMirror() {
    const role=roleFor(state),draft=roleDraft(state),need=NEEDS[draft?.need] || NEEDS.custom;
    const reveal=state.stage==='role' ? 0 : state.stage==='keep' || state.stage==='permission' ? 100 : draft?.reveal ?? 50;
    $('[data-mirror]').style.setProperty('--sm-reveal',reveal+'%');
    const range=$('[data-veil-range]');range.value=String(draft?.reveal ?? 50);range.setAttribute('aria-valuetext',range.value+' percent revealed');
    $('[data-mirror-boundary]').hidden=state.stage!=='unveil';
    $('[data-reveal-controls]').hidden=state.stage!=='unveil';
    $('[data-mirror-caption]').hidden=state.stage==='keep';
    setText('[data-mirror-caption]',!role ? 'Your reflection begins with a choice.' : state.stage==='role' ? role.motto : state.stage==='unveil' ? 'Draw the veil aside.' : 'A little room to act.');
    $('[data-need-symbol] use').setAttribute('href','#SM-'+(need.symbol==='support'?'open':need.symbol)+'-'+uid);
    $('[data-support-art]').hidden=need.symbol!=='support';
    $('[data-need-symbol]').hidden=need.symbol==='support';
    const card=role && deck.find(c=>c.key===role.key);
    $('[data-role-art]').hidden=!card;
    $('[data-archetype]').hidden=!card;
    if(card) {
      for(const selector of ['[data-role-art]','[data-archetype-art]']) if($(selector).getAttribute('src')!==card.image) $(selector).src=card.image;
      setText('[data-archetype-name]',role.roman+' · '+role.archetype);
      setText('[data-archetype-copy]','Use this card as a symbol, not a definition of who you are. '+(copy[role.key+'_question_shadow'] || 'What would you like to notice?'));
    }
    setText('[data-archetype-label]',state.stage==='keep'?'Your tarot symbol +':'About this archetype +');
  }
  function renderNeeds() {
    const fieldset=$('[data-need-options]'),legend=fieldset.querySelector('legend'),role=roleFor(state),draft=roleDraft(state);
    fieldset.replaceChildren(legend);if(!role)return;
    const choices=[...role.needs,['custom','I want to name it myself.']];
    choices.forEach(([key,description])=>{
      const label=root.ownerDocument.createElement('label');label.className='sm-option';
      const input=root.ownerDocument.createElement('input');input.type='radio';input.name='shadow-need';input.value=key;input.dataset.needChoice='';input.checked=draft.need===key;
      const content=root.ownerDocument.createElement('span'),name=root.ownerDocument.createElement('strong'),body=root.ownerDocument.createElement('span');
      name.textContent=NEEDS[key].name;body.textContent=description;content.append(name,body);label.append(input,content);fieldset.append(label);
    });
    setText('[data-current-role]','Your role · '+role.name);
    $('[data-custom-need-field]').hidden=draft.need!=='custom';$('[data-custom-need]').required=draft.need==='custom';$('[data-custom-need]').value=draft.customNeed;
  }
  function syncPermission() {
    const role=roleDraft(state),draft=needDraft(state);if(!draft)return;
    const custom=role.need==='custom';
    all('[data-permission-mode]').forEach(input=>{input.checked=input.value===draft.mode;input.closest('label').hidden=custom && input.value==='keep';});
    setText('[data-default-permission]',NEEDS[role.need].permission);
    setText('[data-permission-need]','Making room for '+(custom?role.customNeed.trim():NEEDS[role.need].name.toLowerCase()));
    $('[data-rewrite-field]').hidden=draft.mode!=='rewrite';$('[data-permission-input]').required=draft.mode==='rewrite';
    $('[data-permission-input]').value=draft.rewrite;
    $('[data-custom-action-field]').hidden=!custom;$('[data-custom-action]').value=draft.customAction;
    clearError($('[data-permission-input]'),$('[data-permission-error]'));
  }
  function renderResult() {
    const result=resultFor(state);if(!result)return;
    const needName=roleDraft(state).need==='custom'?result.need:result.need.toLowerCase();
    setText('[data-result-context]',result.role+' · Making room for '+needName);
    setText('[data-result-permission]',result.permission);setText('[data-result-action]',result.action);
    $('[data-result-action-block]').hidden=!result.action;
    $('[data-entry-one]').value=result.entryOne;$('[data-entry-two]').value=result.entryTwo;
    setText('[data-question-two]',copy[state.role+'_question_shadow'] || 'What else would you like to notice?');
  }
  function go(stage,{focus=true}={}) {
    if(busy)return false;
    if(stage!=='role'&&!state.role)return false;
    if(['permission','keep'].includes(stage)&&!needIsValid(state))return false;
    if(stage==='keep'&&!needDraft(state)?.confirmed)return false;
    closeSave();state.stage=stage;root.dataset.stage=stage;
    all('[data-panel]').forEach(panel=>{panel.hidden=panel.dataset.panel!==stage;});
    const active=stage==='permission'?'keep':stage;
    all('[data-step]').forEach(button=>{if(button.dataset.step===active)button.setAttribute('aria-current','step');else button.removeAttribute('aria-current');});
    if(stage==='unveil')renderNeeds();
    if(stage==='permission')syncPermission();
    if(stage==='keep')renderResult();
    const archetype=$('[data-archetype]');
    if(stage==='keep')$('[data-panel="keep"]').append(archetype);
    else root.querySelector('.sm-visual').append(archetype);
    syncValidity();syncMirror();say('');
    if(focus)$('[data-panel="'+stage+'"] h2').focus({preventScroll:true});
    return true;
  }
  function reveal(value) {
    const draft=roleDraft(state);if(!draft)return;
    draft.reveal=Math.max(0,Math.min(100,Math.round(Number(value)||0)));syncMirror();
  }
  function resetAll() {
    ++epoch;if(busy)setBusy(false);state=newMirrorState();drag=null;delete root.dataset.role;
    all('textarea,input:not([type="radio"]):not([type="range"])').forEach(el=>{el.value='';el.removeAttribute('aria-invalid');});
    all('input[type="radio"]').forEach(input=>{input.checked=false;});
    all('[data-result-context],[data-result-permission],[data-result-action],[data-permission-need],[data-default-permission]').forEach(el=>{el.textContent='';});
    $('[data-reset-confirm]').hidden=true;
    const legend=$('[data-need-options] legend')||root.ownerDocument.createElement('legend');legend.className='sm-sr';legend.textContent='Choose a need';$('[data-need-options]').replaceChildren(legend);
    $('[data-custom-need-field]').hidden=true;
    go('role',{focus:false});saveStatus.textContent='';
  }
  on(root,'change',event=>{
    if(busy)return;
    const el=event.target;
    if(el.matches('[data-role-choice]')) {
      chooseRole(state,el.value);root.dataset.role=state.role;renderNeeds();syncMirror();syncValidity();say('Role selected. You can look beneath when you are ready.');
    } else if(el.matches('[data-need-choice]')) {
      chooseNeed(state,el.value);const draft=roleDraft(state);
      $('[data-custom-need-field]').hidden=el.value!=='custom';$('[data-custom-need]').required=el.value==='custom';$('[data-custom-need]').value=draft.customNeed;
      clearError($('[data-custom-need]'),$('[data-need-error]'));syncMirror();syncValidity();
    } else if(el.matches('[data-permission-mode]')) {
      needDraft(state).mode=el.value;needDraft(state).confirmed=null;syncPermission();syncValidity();
    }
  });
  on(root,'input',event=>{
    if(busy)return;const el=event.target,draft=needDraft(state),role=roleDraft(state);
    if(el.matches('[data-veil-range]')) {reveal(el.value);return;}
    if(el.matches('[data-custom-need]')&&role) {role.customNeed=el.value.slice(0,160);if(draft)draft.confirmed=null;clearError(el,$('[data-need-error]'));}
    if(el.matches('[data-permission-input]')&&draft) {draft.rewrite=el.value.slice(0,400);draft.confirmed=null;clearError(el,$('[data-permission-error]'));}
    if(el.matches('[data-custom-action]')&&draft)draft.customAction=el.value.slice(0,1000);
    if(el.matches('[data-entry-one]')&&draft)draft.entryOne=el.value.slice(0,6000);
    if(el.matches('[data-entry-two]')&&draft)draft.entryTwo=el.value.slice(0,6000);
    if(el.matches('[data-password],[data-password-confirm]')) {el.removeAttribute('aria-invalid');saveStatus.textContent='';}
    syncValidity();
  });
  on($('[data-custom-need]'),'blur',()=>{
    if(roleDraft(state)?.need==='custom'&&!needIsValid(state)) {const field=$('[data-custom-need]');field.setAttribute('aria-invalid','true');setText('[data-need-error]','Name your need using 2–160 characters.');$('[data-need-error]').hidden=false;}
  });
  on($('[data-look]'),'click',()=>go('unveil'));
  on($('[data-choose-permission]'),'click',()=>go('permission'));
  on($('[data-confirm-permission]'),'click',()=>{
    if(busy)return;
    if(!permissionIsValid(state)){error($('[data-permission-input]'),$('[data-permission-error]'),'Write a complete phrase using 2–400 characters.');return;}
    confirmPermission(state);go('keep');
  });
  on($('[data-back-role]'),'click',()=>go('role'));
  on($('[data-back-unveil]'),'click',()=>go('unveil'));
  on($('[data-return]'),'click',()=>go('role'));
  on($('[data-edit-permission]'),'click',()=>go('permission'));
  all('[data-step]').forEach(button=>on(button,'click',()=>go(button.dataset.step==='keep'&&!needDraft(state)?.confirmed?'permission':button.dataset.step)));
  on($('[data-reveal-all]'),'click',()=>{reveal(100);say('The inner image is revealed. Choose what fits.');});
  const handle=$('[data-mirror-drag]');
  on(handle,'pointerdown',event=>{
    if(busy||state.stage!=='unveil'||event.isPrimary===false||event.button!==0)return;
    event.preventDefault();drag={id:event.pointerId,rect:$('[data-mirror-glass]').getBoundingClientRect(),moved:false};
    suppressHandleClick=false;
    handle.setPointerCapture?.(event.pointerId);
  });
  on(handle,'pointermove',event=>{
    if(!drag||drag.id!==event.pointerId)return;
    event.preventDefault();drag.moved=true;const {left,width}=drag.rect;if(width>0)reveal(100*(1-(event.clientX-left)/width));
  });
  const endDrag=event=>{if(drag?.id===event.pointerId){suppressHandleClick=drag.moved||event.type==='pointercancel';drag=null;}};
  for(const name of ['pointerup','pointercancel','lostpointercapture'])on(handle,name,endDrag);
  on(handle,'keydown',event=>{
    const current=roleDraft(state)?.reveal || 0;
    const actions={ArrowLeft:current+5,ArrowRight:current-5,Home:0,End:100};
    if(Object.hasOwn(actions,event.key)){event.preventDefault();reveal(actions[event.key]);}
  });
  on(handle,'click',()=>{if(suppressHandleClick){suppressHandleClick=false;return;}if(!drag)reveal(100);});
  on($('[data-open-save]'),'click',()=>{
    if(busy||!resultFor(state))return;
    $('[data-open-save]').hidden=true;$('[data-open-save]').setAttribute('aria-expanded','true');$('[data-export-panel]').hidden=false;
    $('[data-password]').focus({preventScroll:true});
  });
  on($('[data-cancel-save]'),'click',()=>{if(!busy){closeSave();$('[data-open-save]').focus({preventScroll:true});}});
  on($('[data-save]'),'click',async()=>{
    if(busy||!resultFor(state))return;
    const password=$('[data-password]').value,confirm=$('[data-password-confirm]').value;
    all('[data-password],[data-password-confirm]').forEach(el=>el.removeAttribute('aria-invalid'));
    if(password.length<12||password.length>256){saveStatus.textContent=copy.password_short;$('[data-password]').setAttribute('aria-invalid','true');$('[data-password]').focus({preventScroll:true});return;}
    if(password!==confirm){saveStatus.textContent=copy.password_mismatch;$('[data-password-confirm]').setAttribute('aria-invalid','true');$('[data-password-confirm]').focus({preventScroll:true});return;}
    const text=privateRecord(state),generation=epoch;
    setBusy(true);saveStatus.textContent=copy.encrypting;
    try {
      const html=await encrypt(text,password,copy);if(generation!==epoch)return;
      download(html,win);$('[data-password]').value='';$('[data-password-confirm]').value='';saveStatus.textContent=copy.saved;
    } catch(error) {if(generation===epoch)saveStatus.textContent=error.message===copy.unsupported?copy.unsupported:copy.save_failed;}
    finally {if(generation===epoch)setBusy(false);}
  });
  on($('[data-reset]'),'click',()=>{
    if(busy)return;
    if(hasPrivateWriting(state)){$('[data-reset-confirm]').hidden=false;$('[data-keep]').focus({preventScroll:true});}
    else {resetAll();$('[data-panel="role"] h2').focus({preventScroll:true});}
  });
  on($('[data-clear]'),'click',()=>{if(!busy){resetAll();$('[data-panel="role"] h2').focus({preventScroll:true});}});
  on($('[data-keep]'),'click',()=>{$('[data-reset-confirm]').hidden=true;$('[data-reset]').focus({preventScroll:true});});
  for(const img of all('img'))on(img,'error',()=>{
    img.hidden=true;
    if(img.matches('[data-support-art]'))$('[data-need-symbol]').hidden=false;
  });
  on(win,'pagehide',resetAll);
  on(win.document,'visibilitychange',()=>{if(win.document.hidden)drag=null;});
  on(win.document,'shopify:section:unload',event=>{if(event.target.contains(root)){resetAll();controller.abort();}});
  go('role',{focus:false});
  return {go,reveal,reset:resetAll,get state(){return state;},get busy(){return busy;}};
}
if(typeof document!=='undefined') {
  const mount=()=>document.querySelectorAll('[data-shadow-root]').forEach(root=>initMirror(root));
  if(document.readyState==='loading')document.addEventListener('DOMContentLoaded',mount,{once:true});else mount();
  document.addEventListener('shopify:section:load',mount);
}
